/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IComponentProps, ICoreContext, IDateFormatOptions } from '@msdyn365-commerce/core';
import { ChannelIdentity, format, OrderShipments, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { getOrderChannelAddress, getOrderChannelName } from '@msdyn365-commerce-modules/order-management';
import { countItems } from '@msdyn365-commerce-modules/order-management';
import { getOrderDetailsPageUrl } from '../../get-sales-order';

/**
 * Data required to render each order history row.
 */
export interface IOrderHistoryTableRowData {
    salesOrder: OrderShipments;
    channelIdentities?: ChannelIdentity[];
    orgUnitLocations?: OrgUnitLocation[];
}

/**
 * Resources for order history table rows.
 */
export interface IOrderHistoryTableRowResources {
    orderHistoryViewDetailsButtonText: string;
    orderHistoryViewDetailsButtonAriaLabel: string;
    orderHistoryCreatedDateMobileDescriptionText: string;
    orderHistoryOrderNumberIsNotAvailable: string;

    onlineStoreChannelNameText: string;
    posChannelNameText: string;
}

/**
 * Configuration for the order history rows.
 */
export interface IOrderHistoryTableRowProps extends IComponentProps<IOrderHistoryTableRowData> {
    context: ICoreContext;
    className: string;

    resources: IOrderHistoryTableRowResources;

    shouldShowChannelInfo: boolean;
}

/**
 * Formats amount.
 * @param context - Core context.
 * @param amount - Amount to display.
 * @param currencyCode - Currency for the amount.
 * @returns String value.
 */
const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

/**
 * Formats date.
 * @param context - Core context.
 * @param date - Date to display.
 * @returns String value.
 */
const formatDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    return context.cultureFormatter.formatDate(date, dateOptions);
};

/**
 * Renders rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows for desktop view.
 */
export const OrderHistoryDesktopTableRowCellComponent: React.FC<IOrderHistoryTableRowProps> = (props: IOrderHistoryTableRowProps) => {
    const {
        data: { salesOrder }
    } = props;

    const linkToOrderHistoryDetails = getOrderDetailsPageUrl(props.context.actionContext, props.data.salesOrder);

    const defaultCount = 0;
    const itemsCount = ArrayExtensions.hasElements(salesOrder.SalesLines) ? countItems(salesOrder.SalesLines) : defaultCount;
    const channelName = getOrderChannelName(salesOrder, props.data.channelIdentities);
    const channelAddress = getOrderChannelAddress(salesOrder, props.data.orgUnitLocations);

    const columnWithNumberClassName = `${props.className}__column-with-number`;

    let orderNumberCellText = props.resources.orderHistoryOrderNumberIsNotAvailable;
    if (!StringExtensions.isNullOrWhitespace(salesOrder.SalesId)) {
        orderNumberCellText = salesOrder.SalesId!;
    }

    return (
        <>
            <td>
                <span className={`${props.className}__order-number`}>{orderNumberCellText}</span>
            </td>
            <td>{formatDate(props.context, salesOrder.CreatedDateTime)}</td>
            <td className={columnWithNumberClassName}>{itemsCount}</td>
            <td className={columnWithNumberClassName}>{formatAmount(props.context, salesOrder.TotalAmount, salesOrder.CurrencyCode)}</td>
            {props.shouldShowChannelInfo && (
                <td>
                    {channelName &&
                        (channelAddress
                            ? `${props.resources.posChannelNameText} ${channelName}`
                            : props.resources.onlineStoreChannelNameText)}
                </td>
            )}
            <td>
                <Button
                    className={`${props.className}__view-details-button`}
                    href={linkToOrderHistoryDetails}
                    title={props.resources.orderHistoryViewDetailsButtonText}
                    aria-label={props.resources.orderHistoryViewDetailsButtonAriaLabel}
                >
                    {props.resources.orderHistoryViewDetailsButtonText}
                </Button>
            </td>
        </>
    );
};

/**
 * Renders rows for mobile view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows for mobile view.
 */
export const OrderHistoryMobileTableRowCellComponent: React.FC<IOrderHistoryTableRowProps> = (props: IOrderHistoryTableRowProps) => {
    const {
        data: { salesOrder }
    } = props;

    const linkToOrderHistoryDetails = getOrderDetailsPageUrl(props.context.actionContext, props.data.salesOrder);

    const onClickViewDetails = React.useCallback(() => {
        if (MsDyn365.isBrowser) {
            window.location.href = linkToOrderHistoryDetails;
        }
    }, [linkToOrderHistoryDetails]);

    const orderNumber = StringExtensions.isNullOrWhitespace(salesOrder.SalesId)
        ? props.resources.orderHistoryOrderNumberIsNotAvailable
        : salesOrder.SalesId!;

    const dateText = format(
        props.resources.orderHistoryCreatedDateMobileDescriptionText,
        formatDate(props.context, salesOrder.CreatedDateTime)
    );

    const cellClassName = `${props.className}__mobile-cell`;

    return (
        <>
            <td className={cellClassName}>
                <div
                    className={`${cellClassName}__open-details`}
                    onClick={onClickViewDetails}
                    onKeyPress={onClickViewDetails}
                    role='button'
                    tabIndex={0}
                >
                    <span className={`${cellClassName}__mobile-order-id`}>{orderNumber}</span>
                    <span className={`${cellClassName}__mobile-created-date`}>{dateText}</span>
                </div>
            </td>
            <td>
                <Button
                    className={`${cellClassName}__view-details-button`}
                    href={linkToOrderHistoryDetails}
                    title={props.resources.orderHistoryViewDetailsButtonText}
                    aria-label={props.resources.orderHistoryViewDetailsButtonAriaLabel}
                >
                    {props.resources.orderHistoryViewDetailsButtonText}
                </Button>
            </td>
        </>
    );
};

/**
 * Renders order history rows.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows.
 */
export const OrderHistoryTableRowComponent: React.FC<IOrderHistoryTableRowProps> = (props: IOrderHistoryTableRowProps) => {
    const isMobileView = isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs';

    return (
        <tr className={props.className}>
            {isMobileView ? (
                <OrderHistoryMobileTableRowCellComponent {...props} />
            ) : (
                <OrderHistoryDesktopTableRowCellComponent {...props} />
            )}
        </tr>
    );
};
